/**
 * Shared attachment helpers for every support surface (widget, org-admin
 * queue, platform queue) — they all stream through the same participant-scoped
 * endpoint (`/api/v1/support/attachments/:id`, on the user controller; it
 * authorizes requester OR handler). Everything fetches via apiClient (blob) so
 * the bearer token attaches — a plain href/`<img src>` pointed at that URL
 * would 401 in the browser.
 */

import apiClient from './apiClient';

const fetchBlob = async (fileAssetId: string): Promise<Blob> => {
  const response = await apiClient.get(`/api/v1/support/attachments/${fileAssetId}`, {
    responseType: 'blob',
  });
  return response.data as Blob;
};

/**
 * Returns an object URL for an attachment, for rendering it inline (image
 * previews). The CALLER owns the returned URL and must `URL.revokeObjectURL`
 * it when the element unmounts — these hold the blob in memory until revoked.
 */
export const fetchSupportAttachmentUrl = async (fileAssetId: string): Promise<string> =>
  URL.createObjectURL(await fetchBlob(fileAssetId));

/** Triggers a browser save of the attachment via a throwaway object URL. */
export const downloadSupportAttachment = async (
  fileAssetId: string,
  filename: string,
): Promise<void> => {
  const url = URL.createObjectURL(await fetchBlob(fileAssetId));
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};
